import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { FieldProps } from "react-jsonschema-form";
import styled from "styled-components";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";
import { useProperty } from "../../components/property-provider";
import { useApiClient } from "../../lib/http";
import { useCancellationPoliciesApi } from "../cancellationPolicies/infrastructure/cancellationPolicies.api";
import { defaultFormMessages } from "../shared/crud/form";
import CheckboxGroup from "../witadmin/CRUD/Common/CheckBoxGroup";
import InputGroup from "../witadmin/CRUD/Common/InputGroup";
import SelectGroup from "../witadmin/CRUD/Common/SelectGroup";
import { messages } from "./messages";

const localMessages = defineMessages({
  groupBookingPolicySection: {
    id: "behaviourForm.section.groupBookingPolicySection",
    defaultMessage: "Section {value}",
  },
  groupBookingPolicyMinRooms: {
    id: "behaviourForm.section.groupBookingPolicyMinRooms",
    defaultMessage: "Minimum number of rooms",
  },
  groupBookingPolicyTicker: {
    id: "behaviourForm.section.groupBookingPolicyTicker",
    defaultMessage: "Cancellation policy",
  },
  groupBookingAddSection: {
    id: "behaviourForm.section.groupBookingAddSection",
    defaultMessage: "+ Add section",
  },
});

export interface GroupBookingPolicy {
  policyTicker: string;
  minRooms?: number;
}

const StyledErrorMessage = styled.div`
  margin-top: -0.75em;
  margin-bottom: 1em;
`;

export function GroupBookingModule({ formData, onChange, formContext }: FieldProps) {
  const disabled = formContext?.rootFormData?.allowMultiStayCart === false;
  const intl = useIntl();
  const apiClient = useApiClient();
  const propertyInfo = useProperty();
  const propertyConfiguration = usePropertyConfiguration();
  const { getCancellationPolicies } = useCancellationPoliciesApi(apiClient);
  const [cancellationPolicies, setCancellationPolicies] = useState<
    Record<string, { label: string }> | undefined
  >();
  const groupBookingPolicies: GroupBookingPolicy[] = useMemo(
    () => (formData?.groupBookingPolicies ? JSON.parse(formData?.groupBookingPolicies) : []),
    [formData]
  );
  const showGroupBookingPolicies = typeof cancellationPolicies !== "undefined";

  useEffect(() => {
    if (
      !propertyInfo?.ticker ||
      !propertyConfiguration?.showGroupBookingModule ||
      propertyConfiguration?.showGroupBookingModule !== "1"
    ) {
      return;
    }

    const fetchCancellationPolicies = async () =>
      await getCancellationPolicies(propertyInfo.ticker);
    fetchCancellationPolicies()
      .then(response => {
        if (!response) return;
        const result: Record<string, { label: string }> = response.reduce(
          (acc, policy) => ({
            ...acc,
            [policy.ticker]: { label: policy.ticker },
          }),
          {}
        );
        setCancellationPolicies(result);
      })
      .catch(error => console.error("Error fetching cancellation policies:", error));
  }, [
    getCancellationPolicies,
    propertyInfo?.ticker,
    propertyConfiguration?.showGroupBookingModule,
  ]);

  const handleChange = useCallback(
    (value: string | boolean, type: string) => {
      if (disabled) return;
      const finalValue = typeof value === "string" ? Number(value) : value;
      onChange({
        ...formData,
        [type]: finalValue,
      });
    },
    [disabled, formData, onChange]
  );

  const handleAddPolicy = useCallback(() => {
    if (disabled) return;
    if (!cancellationPolicies) return;
    const newGroupBookingPolicies = [...groupBookingPolicies];
    newGroupBookingPolicies.push({
      policyTicker: Object.keys(cancellationPolicies)[0],
      minRooms: 0,
    });
    onChange({
      ...formData,
      groupBookingPolicies: JSON.stringify(newGroupBookingPolicies),
    });
  }, [disabled, groupBookingPolicies, cancellationPolicies, formData, onChange]);

  const handleRemovePolicy = useCallback(
    (index: number) => {
      if (disabled) return;
      const newGroupBookingPolicies = [...groupBookingPolicies];
      newGroupBookingPolicies.splice(index, 1);
      onChange({
        ...formData,
        groupBookingPolicies: JSON.stringify(newGroupBookingPolicies),
      });
    },
    [disabled, groupBookingPolicies, formData, onChange]
  );

  const handleChangePolicy = useCallback(
    (value: string | boolean, type: string, index: number) => {
      if (disabled) return;
      const newGroupBookingPolicies = [...groupBookingPolicies];
      newGroupBookingPolicies[index][type] =
        type === "minRooms" && typeof value === "string" ? Number(value) : value;
      onChange({
        ...formData,
        groupBookingPolicies: JSON.stringify(newGroupBookingPolicies),
      });
    },
    [disabled, groupBookingPolicies, formData, onChange]
  );

  const groupBookingValue = useMemo(
    () =>
      typeof formData?.groupBookingModule !== "undefined"
        ? Boolean(formData.groupBookingModule)
        : false,
    [formData?.groupBookingModule]
  );

  if (
    !propertyConfiguration?.showGroupBookingModule ||
    propertyConfiguration?.showGroupBookingModule !== "1"
  ) {
    return null;
  }

  return (
    <>
      <div>
        <CheckboxGroup
          value={groupBookingValue}
          onChange={data => handleChange(data.event.target.checked, data.field)}
          field={{
            className: "witadmin-bs3",
            fieldName: "groupBookingModule",
            description: intl.formatMessage(messages.groupBookingModule),
            disabled,
          }}
        />
      </div>
      {groupBookingValue && (
        <>
          <div>
            <label>
              <FormattedMessage {...messages.groupBookingMinGuests} />
            </label>
            <InputGroup
              type="number"
              value={formData?.groupBookingMinGuests || ""}
              field={{ fieldName: "groupBookingMinGuests" }}
              onChange={data => handleChange(data.event.target.value, data.field)}
              disabled={disabled}
              readOnly={disabled}
            />
            {!formData?.groupBookingMinGuests && (
              <StyledErrorMessage>
                <ul className="error-detail bs-callout bs-callout-info">
                  <li className="text-danger">
                    <FormattedMessage {...defaultFormMessages.requiredValidationFailed} />
                  </li>
                </ul>
              </StyledErrorMessage>
            )}
          </div>
          <div>
            <label>
              <FormattedMessage {...messages.groupBookingMaxGuests} />
            </label>
            <InputGroup
              type="number"
              value={formData?.groupBookingMaxGuests || ""}
              field={{ fieldName: "groupBookingMaxGuests" }}
              onChange={data => handleChange(data.event.target.value, data.field)}
              disabled={disabled}
              readOnly={disabled}
            />
            {!formData?.groupBookingMaxGuests && (
              <StyledErrorMessage>
                <ul className="error-detail bs-callout bs-callout-info">
                  <li className="text-danger">
                    <FormattedMessage {...defaultFormMessages.requiredValidationFailed} />
                  </li>
                </ul>
              </StyledErrorMessage>
            )}
            {formData?.groupBookingMaxGuests <= formData?.groupBookingMinGuests && (
              <StyledErrorMessage>
                <ul className="error-detail bs-callout bs-callout-info">
                  <li className="text-danger">
                    <FormattedMessage {...messages.mustBeGreaterThanMinGuests} />
                  </li>
                </ul>
              </StyledErrorMessage>
            )}
          </div>
          <div>
            <label>
              <FormattedMessage {...messages.groupBookingMaxRooms} />
            </label>
            <InputGroup
              type="number"
              value={formData?.groupBookingMaxRooms || ""}
              field={{ fieldName: "groupBookingMaxRooms" }}
              onChange={data => handleChange(data.event.target.value, data.field)}
              disabled={disabled}
              readOnly={disabled}
            />
          </div>
          {showGroupBookingPolicies && (
            <>
              <div className="text-base font-medium mt-4 mb-3">
                <FormattedMessage {...messages.groupBookingPolicies} />
              </div>
              <div>
                {groupBookingPolicies.length > 0 && (
                  <div className="flex flex-col gap-4">
                    {groupBookingPolicies.map((it, idx) => (
                      <div>
                        <div className="mb-2 font-medium">
                          <FormattedMessage
                            {...localMessages.groupBookingPolicySection}
                            values={{ value: idx + 1 }}
                          />
                        </div>
                        <div className="flex gap-8 items-center">
                          <div>
                            <label>
                              <FormattedMessage {...localMessages.groupBookingPolicyMinRooms} />
                            </label>
                            <InputGroup
                              type="number"
                              value={it.minRooms || "0"}
                              field={{ fieldName: "minRooms" }}
                              onChange={data =>
                                handleChangePolicy(data.event.target.value, data.field, idx)
                              }
                              disabled={false}
                              readOnly={false}
                            />
                          </div>
                          <div>
                            <label>
                              <FormattedMessage {...localMessages.groupBookingPolicyTicker} />
                            </label>
                            <SelectGroup
                              value={it.policyTicker}
                              field={{
                                fieldName: "policyTicker",
                                widget: "selector",
                                optionDisplay: cancellationPolicies,
                              }}
                              onChange={data =>
                                handleChangePolicy(data.event.target.value, data.field, idx)
                              }
                              errors={undefined}
                              additionalFields={undefined}
                              className={undefined}
                            />
                          </div>
                          <Button
                            size="sm"
                            disabled={disabled}
                            className="h-[30px] mt-[14px] text-white"
                            onClick={() => handleRemovePolicy(idx)}>
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Button variant="primary" disabled={disabled} onClick={handleAddPolicy}>
                  <FormattedMessage {...localMessages.groupBookingAddSection} />
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}
